/**
 * Backup and Restore Utilities for PeopleTag
 * Functions for backing up and restoring profile data
 */

/**
 * Create a backup of profile data to localStorage
 * @param {Object} profile - Profile to backup
 * @param {string} walletAddress - Wallet address (used as key)
 * @returns {boolean} Success status
 */
export const backupProfileToLocalStorage = (profile, walletAddress) => {
  try {
    if (!profile || !walletAddress) {
      throw new Error('Profile and wallet address are required');
    }

    const backup = {
      profile,
      walletAddress,
      timestamp: Date.now(),
      version: '1.0'
    };

    const key = `peopletag_backup_${walletAddress.toLowerCase()}`;
    localStorage.setItem(key, JSON.stringify(backup));
    
    return true;
  } catch (error) {
    console.error('Error backing up profile:', error);
    return false;
  }
};

/**
 * Restore profile from localStorage backup
 * @param {string} walletAddress - Wallet address
 * @returns {Object|null} Restored profile or null
 */
export const restoreProfileFromLocalStorage = (walletAddress) => {
  try {
    if (!walletAddress) {
      throw new Error('Wallet address is required');
    }

    const key = `peopletag_backup_${walletAddress.toLowerCase()}`;
    const backupData = localStorage.getItem(key);
    
    if (!backupData) {
      return null;
    }

    const backup = JSON.parse(backupData);
    return backup.profile || null;
  } catch (error) {
    console.error('Error restoring profile:', error);
    return null;
  }
};

/**
 * Check if a backup exists for a wallet address
 * @param {string} walletAddress - Wallet address
 * @returns {boolean} Whether backup exists
 */
export const hasBackup = (walletAddress) => {
  if (!walletAddress) return false;
  
  const key = `peopletag_backup_${walletAddress.toLowerCase()}`;
  return localStorage.getItem(key) !== null;
};

/**
 * Get backup metadata (timestamp, version, etc.)
 * @param {string} walletAddress - Wallet address
 * @returns {Object|null} Backup metadata or null
 */
export const getBackupMetadata = (walletAddress) => {
  try {
    if (!walletAddress) return null;

    const key = `peopletag_backup_${walletAddress.toLowerCase()}`;
    const backupData = localStorage.getItem(key);
    
    if (!backupData) return null;

    const backup = JSON.parse(backupData);
    return {
      timestamp: backup.timestamp,
      version: backup.version,
      walletAddress: backup.walletAddress,
      age: Date.now() - backup.timestamp
    };
  } catch (error) {
    console.error('Error getting backup metadata:', error);
    return null;
  }
};

/**
 * Delete backup for a wallet address
 * @param {string} walletAddress - Wallet address
 * @returns {boolean} Success status
 */
export const deleteBackup = (walletAddress) => {
  try {
    if (!walletAddress) return false;

    const key = `peopletag_backup_${walletAddress.toLowerCase()}`;
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Error deleting backup:', error);
    return false;
  }
};

/**
 * List all backups in localStorage
 * @returns {Array} Array of backup metadata
 */
export const listAllBackups = () => {
  const backups = [];
  
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('peopletag_backup_')) {
        try {
          const backupData = JSON.parse(localStorage.getItem(key));
          backups.push({
            walletAddress: backupData.walletAddress,
            timestamp: backupData.timestamp,
            version: backupData.version,
            key
          });
        } catch (e) {
          // Skip invalid backups
        }
      }
    }
  } catch (error) {
    console.error('Error listing backups:', error);
  }

  return backups.sort((a, b) => b.timestamp - a.timestamp);
};

/**
 * Export backup as JSON file
 * @param {string} walletAddress - Wallet address
 * @param {string} filename - Optional filename
 */
export const exportBackupAsFile = (walletAddress, filename = null) => {
  try {
    const backup = restoreProfileFromLocalStorage(walletAddress);
    if (!backup) {
      throw new Error('No backup found for this wallet address');
    }

    const exportData = {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      walletAddress,
      profile: backup
    };

    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `peopletag-backup-${walletAddress.slice(0, 8)}-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting backup:', error);
    throw error;
  }
};

/**
 * Import backup from JSON file
 * @param {File} file - JSON file to import
 * @returns {Promise<Object>} Imported backup data
 */
export const importBackupFromFile = async (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('File is required'));
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        
        if (!data.profile) {
          reject(new Error('Invalid backup format: missing profile data'));
          return;
        }

        resolve({
          profile: data.profile,
          walletAddress: data.walletAddress,
          version: data.version || '1.0'
        });
      } catch (error) {
        reject(new Error(`Failed to parse backup file: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
};

